'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  logoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "bold" (brutalist moderno / streetwear): nombre en condensada
 * gigante (Bebas), kicker mono con cuadrito de acento, tagline en un bloque de
 * color sólido, y la foto en un bloque con borde + "hard shadow" del acento (sin
 * blur). CTAs rectangulares que se "hunden" al presionar. Fondo claro, por lo que
 * la barra superior usa modo sólido (solidNav). Lee tokens del tema (--ms-*).
 */
export function HeroBold({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  logoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (solidNav) */}
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-24 sm:pt-28 pb-12 sm:pb-16">
        <div className={`grid items-end gap-8 sm:gap-10 ${hasMedia ? 'md:grid-cols-[1.35fr_0.9fr]' : ''}`}>
          {/* Columna texto */}
          <div className="min-w-0">
            {/* Kicker mono: cuadrito de acento + ciudad + estado */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--ms-text)' }}>
              <span className="inline-block w-2.5 h-2.5" style={{ background: 'var(--ms-accent)' }} aria-hidden />
              {city && <span>{city}</span>}
              {statusLabel && (
                <>
                  <span className="inline-block w-1 h-1 rounded-full" style={{ background: 'var(--ms-text-muted)' }} aria-hidden />
                  <span style={{ color: statusLabel.tone === 'closed' ? 'var(--ms-text-muted)' : 'var(--ms-text)' }}>
                    {statusLabel.text}
                  </span>
                </>
              )}
            </div>

            {logoUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={logoUrl} alt={businessName} className="h-14 sm:h-20 w-auto object-contain mb-5" />
            )}

            <h1
              className="uppercase font-normal break-words"
              style={{
                fontFamily: 'var(--ms-font-display)',
                color: 'var(--ms-text)',
                fontSize: 'clamp(58px, 13vw, 148px)',
                lineHeight: 0.85,
                letterSpacing: '0.005em',
              }}
            >
              {businessName}
            </h1>

            {/* Tagline en bloque de color sólido */}
            {tagline && (
              <p
                className="inline-block mt-5 px-3 py-1.5 text-[14px] sm:text-[15px] font-bold uppercase tracking-[0.03em]"
                style={{ background: 'var(--ms-accent)', color: '#fff' }}
              >
                {tagline}
              </p>
            )}

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onViewMenu}
                className="inline-flex items-center gap-2 px-7 py-3.5 text-[14px] font-bold uppercase tracking-[0.08em] transition-transform active:translate-x-[4px] active:translate-y-[4px]"
                style={{
                  background: 'var(--ms-accent)',
                  color: '#fff',
                  borderRadius: 'var(--ms-radius)',
                  border: '2px solid var(--ms-border)',
                  boxShadow: '5px 5px 0 0 var(--ms-border)',
                }}
              >
                {t('orderOnline')} →
              </button>
              {hasDelivery && onDelivery && (
                <button
                  type="button"
                  onClick={onDelivery}
                  className="inline-flex items-center px-6 py-3.5 text-[14px] font-bold uppercase tracking-[0.08em] transition-colors hover:bg-[var(--ms-surface-2)]"
                  style={{ color: 'var(--ms-text)', borderRadius: 'var(--ms-radius)', border: '2px solid var(--ms-border)' }}
                >
                  {t('delivery')}
                </button>
              )}
            </div>

            {/* Meta secundaria */}
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--ms-text-muted)' }}>
              <span>{t('pickupAvailable')}</span>
              {pickupWaitMinutes ? <span aria-hidden>·</span> : null}
              {pickupWaitMinutes ? <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span> : null}
            </div>
          </div>

          {/* Columna foto: bloque con borde + hard shadow del acento */}
          {hasMedia && (
            <div
              className="relative w-full overflow-hidden self-stretch min-h-[280px]"
              style={{
                aspectRatio: '4 / 5',
                border: '2px solid var(--ms-border)',
                boxShadow: '8px 8px 0 0 var(--ms-accent)',
                borderRadius: 'var(--ms-radius)',
              }}
            >
              {heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="(max-width: 768px) 100vw, 40vw" className="object-cover" />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
